import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/Navigation';
import CosmicBackground from '../../components/CosmicBackground';
import ToolInterface from '../../components/ToolInterface';
import { getToolById } from '../../data/toolsData';

const FormBuilderPage = () => {
  const tool = getToolById('form-builder');
  const [formType, setFormType] = useState('contact');
  const [formTitle, setFormTitle] = useState('');
  const [fields, setFields] = useState('');
  const [submitText, setSubmitText] = useState('Submit');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState('');

  const formTypes = [
    { id: 'contact', name: 'Contact Form' },
    { id: 'lead', name: 'Lead Capture' },
    { id: 'survey', name: 'Survey Form' },
    { id: 'registration', name: 'Registration Form' },
    { id: 'feedback', name: 'Feedback Form' }
  ];

  const handleGenerate = async () => {
    if (!formTitle.trim()) return;
    
    setIsGenerating(true);
    
    setTimeout(() => {
      let generatedHTML = '';
      
      const customFields = fields.split(',').map(field => field.trim()).filter(field => field);
      
      if (formType === 'contact') {
        generatedHTML = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${formTitle}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Arial', sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
        }
        
        .form-container {
            background: white;
            padding: 40px;
            border-radius: 20px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            width: 100%;
            max-width: 500px;
        }
        
        .form-title {
            text-align: center;
            margin-bottom: 30px;
            color: #333;
            font-size: 28px;
            font-weight: bold;
        }
        
        .form-group {
            margin-bottom: 20px;
        }
        
        .form-group label {
            display: block;
            margin-bottom: 8px;
            color: #555;
            font-weight: 600;
        }
        
        .form-group input,
        .form-group textarea,
        .form-group select {
            width: 100%;
            padding: 12px 15px;
            border: 2px solid #e1e5e9;
            border-radius: 10px;
            font-size: 16px;
            transition: border-color 0.3s ease;
        }
        
        .form-group input:focus,
        .form-group textarea:focus,
        .form-group select:focus {
            outline: none;
            border-color: #667eea;
        }
        
        .form-group textarea {
            resize: vertical;
            min-height: 120px;
        }
        
        .submit-btn {
            width: 100%;
            padding: 15px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            border-radius: 10px;
            font-size: 18px;
            font-weight: bold;
            cursor: pointer;
            transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        
        .submit-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
        }
        
        .required {
            color: #e74c3c;
        }
        
        @media (max-width: 600px) {
            .form-container {
                padding: 30px 20px;
            }
        }
    </style>
</head>
<body>
    <div class="form-container">
        <h1 class="form-title">${formTitle}</h1>
        <form id="contactForm" action="#" method="POST">
            <div class="form-group">
                <label for="name">Full Name <span class="required">*</span></label>
                <input type="text" id="name" name="name" required>
            </div>
            
            <div class="form-group">
                <label for="email">Email Address <span class="required">*</span></label>
                <input type="email" id="email" name="email" required>
            </div>
            
            <div class="form-group">
                <label for="phone">Phone Number</label>
                <input type="tel" id="phone" name="phone">
            </div>
            
            <div class="form-group">
                <label for="subject">Subject <span class="required">*</span></label>
                <input type="text" id="subject" name="subject" required>
            </div>
            
            ${customFields.map((field, index) => `
            <div class="form-group">
                <label for="custom${index}">${field}</label>
                <input type="text" id="custom${index}" name="custom${index}">
            </div>
            `).join('')}
            
            <div class="form-group">
                <label for="message">Message <span class="required">*</span></label>
                <textarea id="message" name="message" placeholder="Tell us how we can help you..." required></textarea>
            </div>
            
            <button type="submit" class="submit-btn">${submitText}</button>
        </form>
    </div>

    <script>
        document.getElementById('contactForm').addEventListener('submit', function(e) {
            e.preventDefault();
            
            // Get form data
            const formData = new FormData(this);
            const data = Object.fromEntries(formData);
            
            // Simple validation
            if (!data.name || !data.email || !data.subject || !data.message) {
                alert('Please fill in all required fields.');
                return;
            }
            
            // Simulate form submission
            alert('Thank you for your message! We will get back to you soon.');
            this.reset();
            
            // Here you would typically send the data to your server
            // fetch('/submit-form', {
            //     method: 'POST',
            //     body: formData
            // });
        });
    </script>
</body>
</html>`;
      } else if (formType === 'lead') {
        generatedHTML = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${formTitle}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Arial', sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
        }
        
        .lead-container {
            background: white;
            padding: 40px;
            border-radius: 20px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            width: 100%;
            max-width: 400px;
            text-align: center;
        }
        
        .lead-title {
            margin-bottom: 15px;
            color: #333;
            font-size: 32px;
            font-weight: bold;
            line-height: 1.2;
        }
        
        .lead-subtitle {
            margin-bottom: 30px;
            color: #666;
            font-size: 18px;
            line-height: 1.4;
        }
        
        .form-group {
            margin-bottom: 20px;
            text-align: left;
        }
        
        .form-group input {
            width: 100%;
            padding: 15px;
            border: 2px solid #e1e5e9;
            border-radius: 10px;
            font-size: 16px;
            transition: border-color 0.3s ease;
        }
        
        .form-group input:focus {
            outline: none;
            border-color: #667eea;
        }
        
        .submit-btn {
            width: 100%;
            padding: 18px;
            background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
            color: white;
            border: none;
            border-radius: 10px;
            font-size: 20px;
            font-weight: bold;
            cursor: pointer;
            transition: transform 0.2s ease, box-shadow 0.2s ease;
            margin-bottom: 15px;
        }
        
        .submit-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 25px rgba(255, 107, 107, 0.3);
        }
        
        .privacy-text {
            color: #999;
            font-size: 12px;
            line-height: 1.4;
        }
        
        .benefits {
            text-align: left;
            margin-bottom: 30px;
        }
        
        .benefit {
            display: flex;
            align-items: center;
            margin-bottom: 10px;
            color: #555;
        }
        
        .benefit::before {
            content: "✓";
            color: #27ae60;
            font-weight: bold;
            margin-right: 10px;
        }
    </style>
</head>
<body>
    <div class="lead-container">
        <h1 class="lead-title">${formTitle}</h1>
        <p class="lead-subtitle">Join thousands who are already transforming their results</p>
        
        <div class="benefits">
            <div class="benefit">Instant access to exclusive content</div>
            <div class="benefit">Weekly tips and strategies</div>
            <div class="benefit">Join our community of achievers</div>
        </div>
        
        <form id="leadForm" action="#" method="POST">
            <div class="form-group">
                <input type="text" id="firstName" name="firstName" placeholder="First Name" required>
            </div>
            
            <div class="form-group">
                <input type="email" id="email" name="email" placeholder="Email Address" required>
            </div>
            
            ${customFields.map((field, index) => `
            <div class="form-group">
                <input type="text" id="custom${index}" name="custom${index}" placeholder="${field}">
            </div>
            `).join('')}
            
            <button type="submit" class="submit-btn">${submitText}</button>
        </form>
        
        <p class="privacy-text">
            We respect your privacy. Unsubscribe at any time.
        </p>
    </div>

    <script>
        document.getElementById('leadForm').addEventListener('submit', function(e) {
            e.preventDefault();
            
            const formData = new FormData(this);
            const data = Object.fromEntries(formData);
            
            if (!data.firstName || !data.email) {
                alert('Please fill in all required fields.');
                return;
            }
            
            // Simulate successful signup
            alert('Welcome! Check your email for your free resources.');
            this.reset();
        });
    </script>
</body>
</html>`;
      } else {
        // Generic form for other types
        generatedHTML = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${formTitle}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: 'Arial', sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
        }
        .form-container {
            background: white;
            padding: 40px;
            border-radius: 20px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            width: 100%;
            max-width: 600px;
        }
        .form-title {
            text-align: center;
            margin-bottom: 30px;
            color: #333;
            font-size: 28px;
            font-weight: bold;
        }
        .form-group {
            margin-bottom: 20px;
        }
        .form-group label {
            display: block;
            margin-bottom: 8px;
            color: #555;
            font-weight: 600;
        }
        .form-group input, .form-group textarea, .form-group select {
            width: 100%;
            padding: 12px 15px;
            border: 2px solid #e1e5e9;
            border-radius: 10px;
            font-size: 16px;
            transition: border-color 0.3s ease;
        }
        .form-group input:focus, .form-group textarea:focus, .form-group select:focus {
            outline: none;
            border-color: #667eea;
        }
        .submit-btn {
            width: 100%;
            padding: 15px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            border-radius: 10px;
            font-size: 18px;
            font-weight: bold;
            cursor: pointer;
            transition: transform 0.2s ease;
        }
        .submit-btn:hover {
            transform: translateY(-2px);
        }
    </style>
</head>
<body>
    <div class="form-container">
        <h1 class="form-title">${formTitle}</h1>
        <form id="customForm" action="#" method="POST">
            ${customFields.map((field, index) => `
            <div class="form-group">
                <label for="field${index}">${field}</label>
                <input type="text" id="field${index}" name="field${index}" required>
            </div>
            `).join('')}
            
            <div class="form-group">
                <label for="email">Email Address</label>
                <input type="email" id="email" name="email" required>
            </div>
            
            <button type="submit" class="submit-btn">${submitText}</button>
        </form>
    </div>

    <script>
        document.getElementById('customForm').addEventListener('submit', function(e) {
            e.preventDefault();
            alert('Form submitted successfully!');
            this.reset();
        });
    </script>
</body>
</html>`;
      }
      
      setResult(generatedHTML);
      setIsGenerating(false);
    }, 2500);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
  };

  const handleDownload = () => {
    const blob = new Blob([result], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formTitle.replace(/\s+/g, '-').toLowerCase()}-form.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
      <CosmicBackground />
      <Navigation />
      
      <section className="pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <ToolInterface
              tool={tool}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              result={result}
              onCopy={handleCopy}
              onDownload={handleDownload}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Form Title *
                  </label>
                  <input
                    type="text"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder="e.g., Contact Us, Get Your Free Guide"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Form Type
                  </label>
                  <select
                    value={formType}
                    onChange={(e) => setFormType(e.target.value)}
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  >
                    {formTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Custom Fields (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={fields}
                    onChange={(e) => setFields(e.target.value)}
                    placeholder="e.g., Company Name, Job Title, Phone Number"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Submit Button Text
                  </label>
                  <input
                    type="text"
                    value={submitText}
                    onChange={(e) => setSubmitText(e.target.value)}
                    placeholder="e.g., Submit, Get Started, Send Message"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
              </div>
            </ToolInterface>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FormBuilderPage;